import React, { useState, useMemo, useEffect } from 'react';
import styled from 'styled-components';
import { Select } from 'bench-internal-theme';
import { showServerError } from 'bk-web-app';
import { toastError } from 'bench-internal-theme';
import { FeatureValue, useFeatureFlags } from '@bench/ff-client';
import { SplitFeatureFlags } from '../../features';
import { displayGroupOptions } from '../ledger-account-sidebar/formOptions';
import useDisplayGroups from '../useDisplayGroups';
import useV2Mutation from 'utils/useV2Mutation';
import {
  UPDATE_LEDGER_ACCOUNT,
  GET_LEDGER_ACCOUNTS,
  GET_LEDGER_ACCOUNTS_BY_CLIENT,
} from '../queries';
import { apolloV2Client } from '../../../general-ledger/queries';
import { useClient } from '../../ClientContext';
import type { LedgerAccount } from '../types';

const DisplayGroupCellLayout = styled.div`
  display: flex;
  align-items: center;
  width: 100%;

  > div {
    width: 100%;
  }
`;

type DisplayGroupCellProps = {
  ledgerAccount: LedgerAccount;
  onSaveStart: () => void;
  onSaveComplete: () => void;
  isSortSaving: boolean;
};

type SelectOption = {
  value: string;
  label: string;
};

const DisplayGroupCell: React.FC<DisplayGroupCellProps> = ({
  ledgerAccount,
  onSaveStart,
  onSaveComplete,
  isSortSaving,
}) => {
  const client = useClient();
  const { displayGroups, isLoading, hasError } = useDisplayGroups();
  const options = displayGroupOptions(displayGroups);

  const initialValue = useMemo(() => {
    if (!ledgerAccount.displayGroup?.id) {
      // Return "Hidden" option for null displayGroup
      return {
        value: null,
        label: 'Hidden',
      };
    }
    return {
      value: ledgerAccount.displayGroup.id,
      label: ledgerAccount.displayGroup.name,
    };
  }, [ledgerAccount.displayGroup]);

  const [selectedValue, setSelectedValue] = useState<SelectOption | null>(initialValue);

  // Sync selectedValue when ledger account's display group changes (e.g., from sidebar update)
  useEffect(() => {
    setSelectedValue(initialValue);
  }, [initialValue]);

  const [featureState] = useFeatureFlags();
  const {
    featureContext: { features },
  } = featureState;

  const isAssignCategoryImprovementEnabled =
    features[SplitFeatureFlags.ASSIGN_CATEGORY_IMPROVEMENT]?.value === FeatureValue.FEATURE_ON;

  const ledgerAccountsQuery = isAssignCategoryImprovementEnabled
    ? GET_LEDGER_ACCOUNTS_BY_CLIENT
    : GET_LEDGER_ACCOUNTS;

  const [updateLedgerAccount, { loading: isSaving }] = useV2Mutation(UPDATE_LEDGER_ACCOUNT, {
    onCompleted: () => {
      onSaveComplete();
    },
    onError: error => {
      onSaveComplete();
      toastError('Failed to update display group', showServerError(error));
      setSelectedValue(initialValue);
    },
    refetchQueries: [
      {
        query: ledgerAccountsQuery,
        variables: { clientId: client.id },
        context: isAssignCategoryImprovementEnabled ? { useV2: true } : undefined,
      },
    ],
    awaitRefetchQueries: true,
    client: isAssignCategoryImprovementEnabled ? apolloV2Client : undefined,
  });

  const handleChange = (option: SelectOption | null) => {
    setSelectedValue(option); // Optimistic update
    onSaveStart();

    const payload = {
      name: ledgerAccount.name,
      details: ledgerAccount.details || null,
      currencyId: ledgerAccount.currencyId,
      categoryId: ledgerAccount.category.id,
      displayGroupId: option?.value || null,
      isTemporary: ledgerAccount.isTemporary,
      functionalInput: ledgerAccount.role
        ? {
            autoPostEnabled: ledgerAccount.autoPostEnabled,
            thirdPartyLoginAvailable: ledgerAccount.thirdPartyLoginAvailable,
            financialInstitutionProductId: ledgerAccount.financialInstitutionProduct?.id
              ? ledgerAccount.financialInstitutionProduct.id
              : null,
            fdsAccountId: null, // Not available in table context
            role: ledgerAccount.role,
            optimizationStatus: ledgerAccount.accountSettings?.optimizationStatus
              ? ledgerAccount.accountSettings.optimizationStatus
              : null,
            collectionMethod: ledgerAccount.accountSettings?.collectionMethod
              ? ledgerAccount.accountSettings.collectionMethod
              : null,
            statementDayOfMonth: ledgerAccount.statementDayOfMonth
              ? ledgerAccount.statementDayOfMonth
              : null,
            dateActive: ledgerAccount.accountSettings?.dateActive
              ? ledgerAccount.accountSettings.dateActive
              : null,
            dateInactive: ledgerAccount.accountSettings?.dateInactive
              ? ledgerAccount.accountSettings.dateInactive
              : null,
            volume: ledgerAccount.accountSettings?.volume
              ? ledgerAccount.accountSettings.volume
              : null,
            isAutoGenerated: ledgerAccount.isAutoGenerated || false,
            accountLastFourDigits: ledgerAccount.accountLastFourDigits
              ? ledgerAccount.accountLastFourDigits
              : null,
            targetLedgerMappings: ledgerAccount.targetLedgerMappings
              ? ledgerAccount.targetLedgerMappings
              : null,
          }
        : null,
      id: ledgerAccount.id,
    };

    updateLedgerAccount({
      variables: {
        ledgerAccountInput: payload,
      },
    }).catch((error: Error) => {
      console.error('DisplayGroupCell: Mutation error', error);
      // Error already handled in onError callback
      // This catch prevents unhandled promise rejection
    });
  };

  const getPlaceholderText = () => {
    if (hasError) {
      return 'Error loading groups';
    }
    return 'Select...';
  };

  return (
    <DisplayGroupCellLayout>
      <Select
        aria-label="Display Group"
        value={selectedValue}
        onChange={handleChange}
        options={options}
        size="small"
        isDisabled={isSortSaving || isSaving || hasError}
        isLoading={isLoading}
        isSearchable
        placeholder={getPlaceholderText()}
      />
    </DisplayGroupCellLayout>
  );
};

export default DisplayGroupCell;
